import { Box, Typography } from '@mui/material'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { useFunctionApps } from '../../api/function-apps-api'
import { useFunctionAppConfiguration } from '../../api/configuration-api'
import { useExecuteHttpFunction, useExecuteTimerFunction } from '../../api/execution-api'
import { useCommandAudits } from '../../api/command-audits-api'
import { HttpFunction, TimerFunction } from '../../types/FunctionApp'
import FunctionAppListPanel from '../../components/FunctionAppListPanel'
import FunctionsTable from './Components/FunctionsTable'
import ExecutionDrawer from './Components/ExecutionDrawer'

type SelectedFunction =
  | { type: 'http'; func: HttpFunction }
  | { type: 'timer'; func: TimerFunction }

const CommandPage = () => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedFunc, setSelectedFunc] = useState<SelectedFunction | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const selectedAppId = searchParams.get('app')
  const setSelectedAppId = (appId: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('app', appId)
      return next
    })
  }

  const [apps] = useFunctionApps()
  const selectedApp = useMemo(
    () => apps.find((a) => a.id === selectedAppId) ?? null,
    [apps, selectedAppId],
  )
  const [configuration, isLoadingConfiguration] = useFunctionAppConfiguration(selectedApp?.id ?? null)
  const [executeHttp, isExecutingHttp, httpResult, resetHttp] = useExecuteHttpFunction()
  const [executeTimer, isExecutingTimer, timerResult, resetTimer] = useExecuteTimerFunction()

  const lastExecutionFilters = useMemo(
    () => ({
      functionAppName: selectedApp?.name,
      functionName: selectedFunc?.func.name,
      type: 'Execution' as const,
      offset: 0,
      limit: 1,
    }),
    [selectedApp?.name, selectedFunc?.func.name],
  )
  const [lastExecutionLogs] = useCommandAudits(lastExecutionFilters, !!selectedApp && !!selectedFunc)
  const lastExecution = lastExecutionLogs[0] ?? null

  const isExecuting = isExecutingHttp || isExecutingTimer
  const executionResult = httpResult ?? timerResult ?? null

  const handleExecuteClick = (selected: SelectedFunction) => {
    setSelectedFunc(selected)
    resetHttp()
    resetTimer()
    setDrawerOpen(true)
  }

  const handleExecuteHttp = async (routeParams: Record<string, string>, headers: Record<string, string>, body: string, contentType: string) => {
    if (!selectedApp || !selectedFunc || selectedFunc.type !== 'http') return

    await executeHttp({
      appId: selectedApp.id,
      functionName: selectedFunc.func.name,
      dto: {
        method: selectedFunc.func.methods[0] ?? 'GET',
        routeParams: Object.keys(routeParams).length > 0 ? routeParams : undefined,
        headers: Object.keys(headers).length > 0 ? headers : undefined,
        body: body || undefined,
        contentType,
      },
    })
  }

  const handleExecuteTimer = async () => {
    if (!selectedApp || !selectedFunc || selectedFunc.type !== 'timer') return

    await executeTimer({
      appId: selectedApp.id,
      functionName: selectedFunc.func.name,
    })
  }

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 100px)' }}>
      <FunctionAppListPanel
        apps={apps}
        selectedApp={selectedApp}
        onSelect={(app) => setSelectedAppId(app.id)}
      />

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', pl: 2 }}>
        {selectedApp ? (
          <FunctionsTable
            appId={selectedApp.id}
            httpFunctions={selectedApp.httpTriggers}
            timerFunctions={selectedApp.timerTriggers}
            configuration={configuration}
            isLoadingConfiguration={isLoadingConfiguration}
            onExecuteHttp={(func) => handleExecuteClick({ type: 'http', func })}
            onExecuteTimer={(func) => handleExecuteClick({ type: 'timer', func })}
          />
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography color="text.secondary">
              {t('command.selectApp', 'Select a function app to view its functions')}
            </Typography>
          </Box>
        )}
      </Box>

      <ExecutionDrawer
        open={drawerOpen}
        selectedFunc={selectedFunc}
        lastPayload={lastExecution?.payload ?? null}
        lastContentType={lastExecution?.contentType ?? null}
        onClose={() => setDrawerOpen(false)}
        onExecuteHttp={handleExecuteHttp}
        onExecuteTimer={handleExecuteTimer}
        isExecuting={isExecuting}
        result={executionResult}
      />
    </Box>
  )
}

export default CommandPage
